import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Button, Spin, Alert, Divider, Typography } from "antd";
import {
  BankOutlined,
  CheckCircleOutlined,
  WhatsAppOutlined,
  MailOutlined,
} from "@ant-design/icons";
import axiosConfig from "../utils/axiosConfig";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import logger from "../utils/logger";

const { Title, Text, Paragraph } = Typography;

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  // بيانات افتراضية للخطط (Backup only)
  const mockPlans = [
    { id: 1, name: "الخطة المجانية", price: 0 },
    { id: 2, name: "الخطة الاحترافية", price: 25000 },
    { id: 3, name: "الخطة المتميزة", price: 45000 },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const planId = location.state?.planId;

        if (!planId) {
          setError("لم يتم تحديد خطة اشتراك، يرجى العودة واختيار خطة");
          setLoading(false);
          return;
        }

        // Fetch Plan Details and System Settings in parallel
        const [planResponse, settingsResponse] = await Promise.all([
          axiosConfig.get(`/subscription/plans/${planId}`).catch(() => null),
          axiosConfig.get("/system-settings/public").catch(() => null),
        ]);

        // Process Plan Data
        if (planResponse && planResponse.data && planResponse.data.success) {
          setPlan(planResponse.data.data);
        } else {
          setPlan(
            mockPlans.find((p) => p.id === parseInt(planId)) || mockPlans[1]
          );
        }

        // Process Settings Data
        if (
          settingsResponse &&
          settingsResponse.data &&
          settingsResponse.data.success
        ) {
          setSettings(settingsResponse.data.data);
        }
      } catch (err) {
        logger.error("Error fetching data:", err);
        setError("حدث خطأ أثناء تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.state]);

  const handleRequestActivation = async () => {
    try {
      setSubmitting(true);

      // Call upgrade endpoint to set status to pending_payment
      const response = await axiosConfig.post("/subscription/upgrade", {
        planId: plan.id,
        paymentMethod: "manual_transfer", // Indicator for backend logs
      });

      if (response.data.success) {
        await Swal.fire({
          title: "تم استلام الطلب",
          text: "تم تسجيل طلبك بنجاح. سيتم تفعيل الحساب بعد التحقق من الدفع.",
          icon: "success",
          confirmButtonText: "حسناً",
        });
        navigate("/subscription-expired");
      } else {
        throw new Error(response.data.message || "فشل تقديم الطلب");
      }
    } catch (err) {
      logger.error("Activation request error:", err);
      Swal.fire({
        title: "فشل تقديم الطلب",
        text:
          err.response?.data?.message ||
          "حدث خطأ أثناء تقديم الطلب، يرجى المحاولة مرة أخرى",
        icon: "error",
        confirmButtonText: "حسناً",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-[calc(100vh-200px)]">
          <Spin size="large" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <Alert
            message="خطأ"
            description={error}
            type="error"
            showIcon
            action={
              <Button
                size="small"
                type="primary"
                onClick={() => navigate("/pricing")}
              >
                العودة للخطط
              </Button>
            }
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 bg-gray-50 min-h-screen" dir="rtl">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              تفعيل الاشتراك
            </h1>
            <p className="text-gray-600">
              لتفعيل اشتراكك، يرجى اتباع تعليمات الدفع أدناه
            </p>
          </div>

          <Card className="shadow-md rounded-lg overflow-hidden border-t-4 border-blue-600">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6 bg-blue-50 p-4 rounded-lg">
                <div>
                  <Text className="text-gray-500 block">الخطة المختارة</Text>
                  <Title level={4} className="m-0 text-blue-700">
                    {plan?.name}
                  </Title>
                </div>
                <div className="text-left">
                  <Text className="text-gray-500 block">المبلغ المستحق</Text>
                  <Title level={3} className="m-0 text-gray-800">
                    {plan?.price?.toLocaleString()}{" "}
                    <small className="text-sm font-normal text-gray-500">
                      د.ع /{" "}
                      {plan?.billing_cycle === "yearly" ? "سنوياً" : "شهرياً"}
                    </small>
                  </Title>
                </div>
              </div>

              <Divider>طرق الدفع المتاحة</Divider>

              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mb-6">
                <div className="flex items-start">
                  <BankOutlined className="text-2xl text-blue-600 ml-3 mt-1" />
                  <div>
                    <Title level={5}>التحويل البنكي</Title>
                    <Paragraph className="whitespace-pre-line text-gray-600 mb-0">
                      {settings?.bank_details ||
                        "يرجى التواصل مع الدعم الفني للحصول على تفاصيل الحساب البنكي."}
                    </Paragraph>
                  </div>
                </div>
              </div>

              <div className="mb-8">
                <Title level={5} className="mb-4">
                  خطوات التفعيل:
                </Title>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold ml-3">
                      1
                    </div>
                    <Text>
                      قم بتحويل المبلغ المذكور أعلاه إلى أحد الحسابات البنكية.
                    </Text>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold ml-3">
                      2
                    </div>
                    <Text>احتفظ بصورة إيصال التحويل.</Text>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold ml-3">
                      3
                    </div>
                    <Text>اضغط على زر "تأكيد الطلب" أدناه.</Text>
                  </div>
                  <div className="flex items-center">
                    <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-bold ml-3">
                      4
                    </div>
                    <Text>
                      تواصل معنا عبر واتساب أو البريد الإلكتروني وأرفق صورة
                      الإيصال لتفعيل حسابك فوراً.
                    </Text>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <Button
                  icon={<WhatsAppOutlined />}
                  className="h-12 flex items-center justify-center text-green-600 border-green-600 hover:bg-green-50"
                  href={`https://wa.me/${settings?.support_phone?.replace(
                    /\+/g,
                    ""
                  )}`}
                  target="_blank"
                >
                  تواصل عبر واتساب
                </Button>
                <Button
                  icon={<MailOutlined />}
                  className="h-12 flex items-center justify-center"
                  href={`mailto:${settings?.support_email}`}
                >
                  {settings?.support_email || "almarjeapro@gmail.com"}
                </Button>
              </div>

              <Button
                type="primary"
                size="large"
                block
                className="h-14 text-lg font-bold bg-gradient-to-l from-blue-600 to-indigo-600 border-0 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
                onClick={handleRequestActivation}
                loading={submitting}
                icon={<CheckCircleOutlined />}
              >
                تأكيد الطلب وإبلاغ الإدارة
              </Button>
              <p className="text-center text-gray-500 mt-2 text-sm">
                سيتم تحويل حالة اشتراكك إلى "بانتظار الدفع" حتى يتم التحقق من
                التحويل.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Checkout;
